// @ts-nocheck
import React, { useState, useEffect, useRef } from 'react';
import { doc, onSnapshot, setDoc, serverTimestamp } from 'firebase/firestore';
import { FaPen, FaCheck, FaTimes, FaPause, FaPlay } from 'react-icons/fa';
import { useAuthState } from 'react-firebase-hooks/auth';
import { getFirebase } from '@services/firebase';
import './PromoBanner.css';

const { auth } = getFirebase();
const getPromoDb = () => getFirebase().db;

const PromoBanner = ({ userRole }) => {
  const [user] = useAuthState(auth);
  const [text, setText] = useState('');
  const [paused, setPaused] = useState(false);
  const [draft, setDraft] = useState('');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  const isAdmin = userRole === 'Admin';
  const isAdminOrSuper = userRole === 'Admin' || userRole === 'Super';
  const isBuyer = userRole === 'Buyer';
  // Visitors (null/undefined role) can see the banner read-only on shared pages
  const isVisitor = !userRole;

  useEffect(() => {
    const promoDb = getPromoDb();
    if (!promoDb) return;
    const promoDoc = doc(promoDb, 'siteConfig', 'promoBanner');
    const unsub = onSnapshot(promoDoc, (snap) => {
      if (snap.exists()) {
        setText(snap.data().text || '');
        setPaused(snap.data().paused === true);
      }
    });
    return () => unsub();
  }, []);

  // Focus input when entering edit mode
  useEffect(() => {
    if (editing && inputRef.current) inputRef.current.focus();
  }, [editing]);

  // Only Admin sees the banner while paused; everyone else (including Super) gets hidden
  if (paused && !isAdmin) return null;
  // Non-admin roles: also hide when there's no text or while admin is editing
  if (!isAdminOrSuper && (!text || editing)) return null;

  const startEdit = () => {
    setDraft(text);
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setDraft('');
  };

  const togglePaused = async () => {
    try {
      await setDoc(doc(getPromoDb(), 'siteConfig', 'promoBanner'), { paused: !paused, updatedAt: serverTimestamp() }, { merge: true });
    } catch (e) {
      console.error('Failed to toggle banner pause:', e);
    }
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await setDoc(doc(getPromoDb(), 'siteConfig', 'promoBanner'), { text: draft.trim(), updatedAt: serverTimestamp() }, { merge: true });
      setEditing(false);
    } catch (e) {
      console.error('Failed to save promo text:', e);
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') saveEdit();
    if (e.key === 'Escape') cancelEdit();
  };

  // Admin/Super in edit mode
  if (isAdminOrSuper && editing) {
    return (
      <div className="promo-banner promo-banner--editing">
        <input
          ref={inputRef}
          className="promo-edit-input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Enter promotion text…"
          maxLength={300}
        />
        <button
          className="promo-icon-btn promo-save-btn"
          onClick={saveEdit}
          disabled={saving}
          title="Save"
        >
          <FaCheck size={14} />
        </button>
        <button
          className="promo-icon-btn promo-cancel-btn"
          onClick={cancelEdit}
          title="Cancel"
        >
          <FaTimes size={14} />
        </button>
      </div>
    );
  }

  // No text yet — only Admin/Super see the empty state with edit button
  if (!text) {
    if (!isAdminOrSuper) return null;
    return (
      <div className="promo-banner promo-banner--empty">
        <span className="promo-empty-label">No promotion set</span>
        <button className="promo-icon-btn promo-edit-btn" onClick={startEdit} title="Edit promotion">
          <FaPen size={14} />
        </button>
      </div>
    );
  }

  // Running marquee (visible to Buyer, Admin, Super; paused hides from non-admins)
  return (
    <div className={`promo-banner${paused ? ' promo-banner--paused' : ''}`}>
      {isAdminOrSuper && (
        <button className="promo-icon-btn promo-playpause-btn" onClick={togglePaused} title={paused ? 'Resume banner' : 'Pause banner'}>
          {paused ? <FaPlay size={11} /> : <FaPause size={11} />}
        </button>
      )}
      <div className="promo-track-wrapper">
        <div className="promo-track" style={paused ? { animationPlayState: 'paused' } : undefined}>
          <span className="promo-text">{text}&nbsp;&nbsp;&nbsp;★&nbsp;&nbsp;&nbsp;{text}&nbsp;&nbsp;&nbsp;★&nbsp;&nbsp;&nbsp;</span>
          <span className="promo-text" aria-hidden="true">{text}&nbsp;&nbsp;&nbsp;★&nbsp;&nbsp;&nbsp;{text}&nbsp;&nbsp;&nbsp;★&nbsp;&nbsp;&nbsp;</span>
        </div>
      </div>
      {isAdminOrSuper && (
        <button className="promo-icon-btn promo-edit-btn" onClick={startEdit} title="Edit promotion">
          <FaPen size={14} />
        </button>
      )}
    </div>
  );
};

export default PromoBanner;
